import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../AuthContext";

// Canonical role list in display order. Keep in sync with AuthContext.normalizeRole().
const ROLES = ["superAdmin", "admin", "staff", "vip", "user", "client", "vendor", "partner"];

const ROLE_LABELS = {
  superAdmin: "Super Admin",
  admin:      "Admin",
  staff:      "Staff",
  vip:        "VIP",
  user:       "User",
  client:     "Client",
  vendor:     "Vendor",
  partner:    "Partner",
};
const roleLabel = (role) => ROLE_LABELS[role] || "Client";

export default function AdminUsers() {
  const { currentUser, isAdmin, isSuperAdmin } = useAuth();

  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleRoleChange = async (userId, newRole, currentRole) => {
    // Permanent Super Admin accounts cannot be reassigned by anyone.
    if (currentRole === "superAdmin") return;

    setSavingId(userId);
    try {
      await updateDoc(doc(db, "users", userId), { role: newRole });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
      );
    } catch (err) {
      console.error(err);
      alert("Failed to update role. Check your permissions.");
    } finally {
      setSavingId(null);
    }
  };

  // Defense in depth: block render if somehow a non-admin reaches this tab.
  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", color: "#fff", textAlign: "center" }}>
        <p style={{ fontSize: "1.1rem", opacity: 0.7 }}>
          🔒 User management is restricted to admins.
        </p>
      </div>
    );
  }

  if (loading) return <p className="admin-loading">Loading users...</p>;

  return (
    <div className="bookings-table-wrap">
      <table className="bookings-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Current Role</th>
            <th>Points</th>
            {isSuperAdmin && <th>Change Role</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const displayRole = u.role === "superadmin" ? "superAdmin"
                               : u.role === "customer"   ? "client"
                               : ROLES.includes(u.role)  ? u.role
                               : "client";
            return (
              <tr key={u.id}>
                <td>{u.name || "—"}</td>
                <td>{u.email}</td>
                <td>
                  <span className={`role-pill role-${displayRole.toLowerCase()}`}>
                    {roleLabel(displayRole)}
                  </span>
                </td>
                <td>{u.rewards?.currentPoints ?? 0}</td>
                {isSuperAdmin && (
                  <td>
                    {displayRole === "superAdmin" ? (
                      <span
                        title="Super Admin role is permanent and cannot be changed"
                        style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic", userSelect: "none" }}
                      >
                        🔒 Permanent
                      </span>
                    ) : (
                      <select
                        className="role-select"
                        value={displayRole}
                        disabled={savingId === u.id}
                        onChange={(e) => handleRoleChange(u.id, e.target.value, displayRole)}
                      >
                        {ROLES.filter((r) => r !== "superAdmin").map((role) => (
                          <option key={role} value={role}>
                            {roleLabel(role)}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
